import { useNotification } from "@context/NotificationContext";
import { hasOperationsNotifyRole } from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/utils";
import {
  generatePrefixedNotifyList,
  getPortServiceStatusInfo,
  STATUS_KEYS,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import type {
  MarpolAnnexI,
  Resource,
} from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import type {
  ChangePortServiceStatusPayload,
  PortServiceResourceEquipment,
} from "@services/port-services/types";
import { changePortServiceStatus, sendPortServicesDocuments } from "@services/portServicesApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";

interface UsePortServiceProps {
  status: string;
  serviceCategory?: string;
  notifyList: string[];
  portServiceId: string;
  ats: string | Date | null;
  atc: string | Date | null;
  etc: string | Date | null;
  ets: string | Date | null;
  numberOfSkips: string | number | null;
  quantity: string | number | null;
  numberOfPackagesHandled: number | null;
  chargedParty: string | null;
  spares: number | null;
  incidents: string | null;
  resourcesAndEquipment: PortServiceResourceEquipment[];
  internalNotes: string | null;
  externalNotes: string | null;
  rejectionReason: string | null;
  garbageDisposalDocument: { documentUrl: string; documentName: string; fileType: string };
  billOfLading: { documentUrl: string; documentName: string; fileType: string };
  cargoType: string | null;
  operationType: string | null;
  resources?: Resource[] | null;
  serviceReportAttachment: { documentUrl: string; documentName: string; fileType: string };
  attachment: { documentUrl: string; documentName: string; fileType: string };
  wasteResiduesForDischargeFacility: MarpolAnnexI[] | null;
  specialInstructions: string | null;
  notes: string | null;
  slopsDisposalDocuments: { documentUrl: string; documentName: string; fileType: string }[];
  internalAttachment: { documentUrl: string; documentName: string; fileType: string };
  externalAttachment: { documentUrl: string; documentName: string; fileType: string };
  rejectionAttachment: { documentUrl: string; documentName: string; fileType: string };
  images: { documentUrl: string; documentName: string; fileType: string }[];
  needsOperationsApproval?: boolean;
  useUsageTimeKeysForResources?: boolean;
  setShouldRefetch: (value: boolean) => void;
  onClose: () => void;
}
const LANG_BASE = "vessel-call.port-services";

const serializeOffsetDateTime = (value: string | Date | null | undefined) => {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const serializeResourcesAndEquipment = (
  resourcesAndEquipment: PortServiceResourceEquipment[],
  useUsageTimeKeysForResources: boolean,
) =>
  resourcesAndEquipment.map((resource) => {
    const serializedStartTime = serializeOffsetDateTime(resource.startTimeOfUsage);
    const serializedEndTime = serializeOffsetDateTime(resource.endTimeOfUsage);

    if (useUsageTimeKeysForResources) {
      const { startTimeOfUsage, endTimeOfUsage, ...rest } = resource;

      return {
        ...rest,
        usageStartTime: serializedStartTime,
        usageEndTime: serializedEndTime,
      };
    }

    return {
      ...resource,
      startTimeOfUsage: serializedStartTime,
      endTimeOfUsage: serializedEndTime,
    };
  });

const getPortServiceStatusAction = (status: string): string => {
  if (status.includes("APPROVED")) return "approve_port_service";
  if (status.includes("REJECTED")) return "reject_port_service";
  if (status.includes("STARTED")) return "start_port_service";
  if (status.includes("CLOSED")) return "close_port_service";
  return "change_port_service_status";
};

export interface UsePortServiceResult {
  onSubmit: () => Promise<void>;
}

export const useChangePortServiceStatus = (props: UsePortServiceProps): UsePortServiceResult => {
  const {
    status,
    serviceCategory,
    portServiceId,
    notifyList,
    ats,
    atc,
    etc,
    ets,
    numberOfSkips,
    quantity,
    numberOfPackagesHandled,
    chargedParty,
    spares,
    incidents,
    resourcesAndEquipment = [],
    internalNotes,
    externalNotes,
    rejectionReason,
    garbageDisposalDocument,
    billOfLading,
    cargoType,
    operationType,
    resources,
    serviceReportAttachment,
    slopsDisposalDocuments,
    internalAttachment,
    externalAttachment,
    rejectionAttachment,
    images,
    specialInstructions,
    wasteResiduesForDischargeFacility,
    attachment,
    notes,
    needsOperationsApproval,
    useUsageTimeKeysForResources = false,
    setShouldRefetch,
    onClose,
  } = props;
  const { notify } = useNotification();

  const changeStatus = useMutation<unknown, unknown, ChangePortServiceStatusPayload>({
    mutationFn: async (payload) => {
      if (garbageDisposalDocument.documentUrl) {
        const documentsPayload = {
          documents: [
            {
              portServiceId,
              url: garbageDisposalDocument.documentUrl,
              name: garbageDisposalDocument.documentName,
              originalFileName: garbageDisposalDocument.documentName,
              expirationDate: null,
              type: garbageDisposalDocument.fileType,
            },
          ],
          notificationFor: generatePrefixedNotifyList("FILE_UPLOADED", notifyList),
        };

        await runTransaction(
          {
            module: "vessel_call",
            screen: "PortServiceStatus",
            action: "send_port_service_document",
            entityType: "port_service_document",
          },
          (transactionId) =>
            sendPortServicesDocuments(
              "bff-vessel-call",
              documentsPayload,
              ...(transactionId ? [transactionId] : []),
            ),
        );
      }

      if (Array.isArray(images) && images.length) {
        const imageDocuments = images
          .filter((img) => Boolean(img?.documentUrl))
          .map((img) => ({
            portServiceId,
            url: img.documentUrl,
            name: img.documentName,
            originalFileName: img.documentName,
            expirationDate: null,
            type: img.fileType,
          }));

        if (imageDocuments.length) {
          const documentsPayload = {
            documents: imageDocuments,
            notificationFor: generatePrefixedNotifyList("FILE_UPLOADED", notifyList),
          };

          await runTransaction(
            {
              module: "vessel_call",
              screen: "PortServiceStatus",
              action: "send_port_service_document",
              entityType: "port_service_document",
            },
            (transactionId) =>
              sendPortServicesDocuments(
                "bff-vessel-call",
                documentsPayload,
                ...(transactionId ? [transactionId] : []),
              ),
          );
        }
      }

      if (serviceReportAttachment.documentUrl) {
        const documentsPayload = {
          documents: [
            {
              portServiceId,
              url: serviceReportAttachment.documentUrl,
              name: serviceReportAttachment.documentName,
              originalFileName: serviceReportAttachment.documentName,
              expirationDate: null,
              type: serviceReportAttachment.fileType,
            },
          ],
          notificationFor: generatePrefixedNotifyList("FILE_UPLOADED", notifyList),
        };

        await runTransaction(
          {
            module: "vessel_call",
            screen: "PortServiceStatus",
            action: "send_port_service_document",
            entityType: "port_service_document",
          },
          (transactionId) =>
            sendPortServicesDocuments(
              "bff-vessel-call",
              documentsPayload,
              ...(transactionId ? [transactionId] : []),
            ),
        );
      }

      if (Array.isArray(slopsDisposalDocuments) && slopsDisposalDocuments.length) {
        const imageDocuments = slopsDisposalDocuments
          .filter((img) => Boolean(img?.documentUrl))
          .map((img) => ({
            portServiceId,
            url: img.documentUrl,
            name: img.documentName,
            originalFileName: img.documentName,
            expirationDate: null,
            type: img.fileType,
          }));

        if (imageDocuments.length) {
          const documentsPayload = {
            documents: imageDocuments,
            notificationFor: generatePrefixedNotifyList("FILE_UPLOADED", notifyList),
          };

          await runTransaction(
            {
              module: "vessel_call",
              screen: "PortServiceStatus",
              action: "send_port_service_document",
              entityType: "port_service_document",
            },
            (transactionId) =>
              sendPortServicesDocuments(
                "bff-vessel-call",
                documentsPayload,
                ...(transactionId ? [transactionId] : []),
              ),
          );
        }
      }

      return runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceStatus",
          action: getPortServiceStatusAction(status),
          entityType: "port_service",
        },
        (transactionId) =>
          changePortServiceStatus(
            "bff-vessel-call",
            portServiceId,
            payload,
            ...(transactionId ? [transactionId] : []),
          ),
      );
    },
    onSuccess: () => {
      setShouldRefetch?.(true);
      onClose();
      notify({
        title: t(`${LANG_BASE}.notifications.service-status-change-success-title`),
        description: t(`${LANG_BASE}.notifications.service-status-change-success-description`, {
          newStatus: getPortServiceStatusInfo(status, serviceCategory).label,
        }),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t(`${LANG_BASE}.notifications.service-status-change-error-title`),
        description: t(`${LANG_BASE}.notifications.service-status-change-error-description`),
        type: "error",
      });
      console.error("Error submitting port service request:", error);
    },
  });

  const onSubmit = async (): Promise<void> => {
    const notificationFor = generatePrefixedNotifyList("STATUS_CHANGED", notifyList);
    const resolvedNeedsOperationsApproval =
      needsOperationsApproval !== undefined && status === STATUS_KEYS.MD_APPROVED
        ? needsOperationsApproval || hasOperationsNotifyRole([...notifyList, ...notificationFor])
        : needsOperationsApproval;

    const payload = {
      status,
      notificationFor,
      ats: ats ?? null,
      atc: atc ?? null,
      etc: etc ?? null,
      ets: ets ?? null,
      numberOfSkips: numberOfSkips ?? null,
      quantity: quantity ?? null,
      numberOfPackagesHandled,
      chargedParty: chargedParty || undefined,
      spares,
      incidents: incidents || undefined,
      resourcesAndEquipment: resourcesAndEquipment.length
        ? serializeResourcesAndEquipment(resourcesAndEquipment, useUsageTimeKeysForResources)
        : undefined,
      ...(resolvedNeedsOperationsApproval !== undefined
        ? { needsOperationsApproval: resolvedNeedsOperationsApproval }
        : null),
      internalNotes: internalNotes || undefined,
      specialInstructions: specialInstructions || undefined,
      ...(wasteResiduesForDischargeFacility?.length ? { wasteResiduesForDischargeFacility } : null),
      notes: notes || undefined,
      cargoType: cargoType || undefined,
      operationType: operationType || undefined,
      ...(resources?.length ? { resourcesAndEquipment: resources } : null),
      internalAttachment: internalAttachment.documentUrl
        ? {
            url: internalAttachment.documentUrl,
            name: internalAttachment.documentName,
            fileType: internalAttachment.fileType,
          }
        : undefined,
      billOfLadingAttachment: billOfLading.documentUrl
        ? {
            url: billOfLading.documentUrl,
            name: billOfLading.documentName,
            fileType: billOfLading.fileType,
          }
        : undefined,
      externalNotes: externalNotes || undefined,
      externalAttachment: externalAttachment.documentUrl
        ? {
            url: externalAttachment.documentUrl,
            name: externalAttachment.documentName,
            fileType: externalAttachment.fileType,
          }
        : attachment.documentUrl
          ? {
              url: attachment.documentUrl,
              name: attachment.documentName,
              fileType: attachment.fileType,
            }
          : undefined,
      rejectionReason: rejectionReason || undefined,
      rejectionAttachment: rejectionAttachment.documentUrl
        ? {
            url: rejectionAttachment.documentUrl,
            name: rejectionAttachment.documentName,
            fileType: rejectionAttachment.fileType,
          }
        : undefined,
      closureImages:
        slopsDisposalDocuments && slopsDisposalDocuments.length
          ? slopsDisposalDocuments.map((document) => ({
              url: document.documentUrl,
              name: document.documentName,
              fileType: document.fileType,
            }))
          : undefined,
      documents:
        serviceReportAttachment && serviceReportAttachment.documentUrl
          ? {
              url: serviceReportAttachment.documentUrl,
              name: serviceReportAttachment.documentName,
              fileType: serviceReportAttachment.fileType,
            }
          : undefined,
    };
    changeStatus.mutate(payload);
  };
  return { onSubmit };
};
